import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { AuditEntry } from "../../../../types/onboarding";

interface AuditHistoryTableProps {
  audit: AuditEntry[];
}

// Map raw contract field names to human-readable labels (FR-024). Unknown
// fields fall back to a spaced/capitalised form rather than the raw camelCase.
const FIELD_LABELS: Record<string, string> = {
  firstName: "First name",
  lastName: "Last name",
  email: "Email",
  telephone: "Phone",
  companyName: "Company name",
  vatNumber: "VAT number",
  billingStreet: "Billing street",
  billingPostcode: "Billing postcode",
  billingCity: "Billing city",
  billingCountry: "Billing country",
  stationStreet: "Station street",
  stationPostcode: "Station postcode",
  stationCity: "Station city",
  stationCountry: "Station country",
  latitude: "Latitude",
  longitude: "Longitude",
  chargePointCount: "Charging points",
  stationCount: "Wallboxes",
  stationEvseCounts: "Charging points per wallbox",
  wallboxManufacturer: "Manufacturer",
  wallboxModel: "Model",
  accountHolderName: "Account holder",
  iban: "IBAN",
  bic: "BIC",
  bankName: "Bank name",
  discoverySource: "How they found us",
  // Not field edits but recorded events, written in SCREAMING_SNAKE. Without
  // these the fallback below spaced every capital — "K E Y_ R E V E A L E D".
  KEY_REVEALED: "Access key revealed",
  // Granting the customer ownership of their own station, recorded per station
  // so a partial failure is visible after the fact too (CWA-154 finding 7).
  OWNER_PERMISSION_ASSIGNED: "Station access granted",
  OWNER_PERMISSION_FAILED: "Station access not granted",
  GO_LIVE_TRIGGERED: "Marked as live",
  // The onboarding setup fee (CWA-196).
  SETUP_FEE_INVOICED: "Setup fee invoiced",
  SETUP_FEE_WAIVED: "Setup fee waived",
  SETUP_FEE_CONSENT_GIVEN: "Setup-fee consent given",
};

const humaniseField = (name: string): string => {
  if (FIELD_LABELS[name]) return FIELD_LABELS[name];
  // SCREAMING_SNAKE first: splitting it on capitals mangles every word.
  if (/^[A-Z0-9]+(_[A-Z0-9]+)*$/.test(name)) {
    return name
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^./, (c) => c.toUpperCase());
  }
  return name.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
};

const roleLabel = (role: string): string =>
  role === "OPS" ? "Ops" : role === "CUSTOMER" ? "Customer" : "System";

const valueDisplay = (entry: AuditEntry, value: string | null): string => {
  if (entry.is_sensitive) return "•••";
  return value ?? "—";
};

/**
 * Per-field edit-audit history (FR-009), most-recent first. Sensitive values
 * are masked in the cell while the row remains visible to authorised ops.
 */
const AuditHistoryTable: React.FC<AuditHistoryTableProps> = ({ audit }) => {
  if (!audit?.length) {
    return <p className="text-500">No edit history yet.</p>;
  }

  const ordered = [...audit].sort(
    (a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
  );

  return (
    <DataTable value={ordered} paginator rows={10} className="mt-2">
      <Column
        header="When"
        body={(e: AuditEntry) => new Date(e.changed_at).toLocaleString()}
      />
      <Column header="Field" body={(e: AuditEntry) => humaniseField(e.field_name)} />
      <Column
        header="From"
        body={(e: AuditEntry) => valueDisplay(e, e.prior_value)}
      />
      <Column
        header="To"
        body={(e: AuditEntry) => valueDisplay(e, e.new_value)}
      />
      <Column header="Changed by" body={(e: AuditEntry) => roleLabel(e.actor_role)} />
    </DataTable>
  );
};

export default AuditHistoryTable;
